import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { t } from '@lingui/macro';
import { H1 } from '@decisiv/ui-components/lib/components/Typography';
import Plus from '@decisiv/iconix/lib/components/Plus';
import Search from '@decisiv/iconix/lib/components/Search';
import Upload from '@decisiv/iconix/lib/components/Upload';
import ChevronLeft from '@decisiv/iconix/lib/components/ChevronLeft';
import { CATEGORY_META, getCategoryMeta, currentVersion } from './atUtils';
import { ToolkitButton } from './style';
import authorizeOn from '../../utils/resourceAuthorizer';

// Page toolbar: search, category chips, Import + Create. Import re-creates a
// single exported action (create-only); automatic sync-from-prod is deferred.
export function PageToolbar({ filter, onFilter, onCreate, onImport }) {
  const categories = CATEGORY_META.filter((c) => c.name !== 'Uncategorized');
  // Import and Create hit admin-only endpoints; a non-global-admin who triggers
  // them gets a 401 that logs the session out (PD-3396), so disable them here.
  const canManage = authorizeOn();

  return (
    <div className="page-toolbar">
      <div className="page-title-row">
        <H1 className="page-title">{t`Actions`}</H1>
        <div className="actions">
          <ToolkitButton
            kind="secondary"
            icon={Upload}
            text={t`Import`}
            onClick={onImport}
            disabled={!canManage}
          />
          <ToolkitButton
            kind="primary"
            icon={Plus}
            text={t`Create Action`}
            onClick={onCreate}
            disabled={!canManage}
          />
        </div>
      </div>
      <div className="toolbar-row">
        <div className="search-box">
          <span className="icon">
            <Search size="small" />
          </span>
          <input
            type="search"
            placeholder={t`Search actions by name or alias…`}
            value={filter.search}
            onChange={(e) => onFilter({ ...filter, search: e.target.value })}
          />
        </div>
        <span className="filter-label">{t`Filter by:`}</span>
        <div className="chips">
          {categories.map((cat) => {
            const active = filter.categories.includes(cat.name);
            const CatIcon = cat.icon;
            return (
              <button
                type="button"
                key={cat.name}
                className={`chip ${active ? 'active' : ''}`}
                style={{
                  '--chip-color': cat.color,
                  '--chip-bg': cat.bg,
                  '--chip-fg': 'var(--if-on-vivid)',
                }}
                onClick={() =>
                  onFilter({
                    ...filter,
                    categories: active
                      ? filter.categories.filter((c) => c !== cat.name)
                      : [...filter.categories, cat.name],
                  })
                }
              >
                <span className="chip-dot">
                  <CatIcon size="small" />
                </span>
                {cat.name}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

PageToolbar.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  filter: PropTypes.object.isRequired,
  onFilter: PropTypes.func.isRequired,
  onCreate: PropTypes.func.isRequired,
  onImport: PropTypes.func.isRequired,
};

// Left list pane: filtered scroll of action rows, grouped by category with
// collapsible sections. Defaults: closed; auto-open if the group contains the
// current selection or any filter is active. Once the user toggles a group,
// that explicit choice wins until they toggle it again.
export function ListPane({
  actions,
  selectedId = null,
  onSelect,
  filter,
  onFilter,
}) {
  const filtered = useMemo(() => {
    let out = actions;
    if (filter.search) {
      const q = filter.search.toLowerCase();
      out = out.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          (a.alias || '').toLowerCase().includes(q),
      );
    }
    if (filter.categories.length > 0) {
      out = out.filter((a) => filter.categories.includes(a.category));
    }
    return out;
  }, [actions, filter]);

  const groups = useMemo(() => {
    const byCategory = new Map();
    filtered.forEach((a) => {
      const key = a.category || 'Uncategorized';
      if (!byCategory.has(key)) byCategory.set(key, []);
      byCategory.get(key).push(a);
    });
    return CATEGORY_META.map((meta) => ({
      meta,
      items: byCategory.get(meta.name) || [],
    })).filter((g) => g.items.length > 0);
  }, [filtered]);

  const selectedCategory = useMemo(() => {
    const sel = actions.find((a) => a.id === selectedId);
    return sel?.category || null;
  }, [actions, selectedId]);

  const filterActive = Boolean(filter.search) || filter.categories.length > 0;
  const [overrides, setOverrides] = useState(() => new Map());

  const isOpen = (name) => {
    if (overrides.has(name)) return overrides.get(name);
    if (filterActive) return true;
    return name === selectedCategory;
  };

  const toggleGroup = (name) => {
    const next = new Map(overrides);
    next.set(name, !isOpen(name));
    setOverrides(next);
  };

  const expandAll = () => {
    const next = new Map();
    groups.forEach((g) => next.set(g.meta.name, true));
    setOverrides(next);
  };
  const collapseAll = () => {
    const next = new Map();
    groups.forEach((g) => next.set(g.meta.name, false));
    setOverrides(next);
  };
  const allOpen = groups.every((g) => isOpen(g.meta.name));

  return (
    <aside className="list-pane">
      <div className="list-header">
        <span className="count">
          {filtered.length} {filtered.length === 1 ? t`action` : t`actions`}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {groups.length > 0 && (
            <ToolkitButton
              variant="ghost"
              size="small"
              text={allOpen ? t`Collapse all` : t`Expand all`}
              onClick={allOpen ? collapseAll : expandAll}
            />
          )}
          {filterActive && (
            <ToolkitButton
              variant="ghost"
              size="small"
              text={t`Clear filters`}
              onClick={() => onFilter({ search: '', categories: [] })}
            />
          )}
        </div>
      </div>
      <div className="list-scroll">
        {filtered.length === 0 && (
          <div
            style={{
              padding: '32px 20px',
              textAlign: 'center',
              color: 'var(--kds-text-3)',
              fontSize: 13,
            }}
          >
            {t`No actions match these filters.`}
          </div>
        )}
        {filtered.length > 0 &&
          groups.map(({ meta, items }) => {
            const open = isOpen(meta.name);
            const GroupIcon = meta.icon;
            return (
              <section key={meta.name} className="at-group">
                <div
                  className="at-group-header"
                  onClick={() => toggleGroup(meta.name)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleGroup(meta.name);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-expanded={open}
                >
                  <span className={`at-group-chev ${open ? 'open' : ''}`}>
                    <ChevronLeft size="small" />
                  </span>
                  <span
                    className="at-group-icon"
                    style={{
                      '--group-color': meta.color,
                      '--group-bg': meta.bg,
                    }}
                  >
                    <GroupIcon size="small" />
                  </span>
                  <span className="at-group-name">{meta.name}</span>
                  <span className="at-group-count">{items.length}</span>
                </div>
                {open &&
                  items.map((action) => {
                    const cat = getCategoryMeta(action.category);
                    const cur = currentVersion(action);
                    const RowIcon = cat.icon;
                    return (
                      <div
                        key={action.id}
                        className={`action-row ${
                          selectedId === action.id ? 'selected' : ''
                        }`}
                        style={{
                          '--row-color': cat.color,
                          '--row-fg': 'var(--if-on-vivid)',
                        }}
                        onClick={() => onSelect(action.id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) =>
                          e.key === 'Enter' ? onSelect(action.id) : null
                        }
                      >
                        <span className="row-icon">
                          <RowIcon size="small" />
                        </span>
                        <div className="row-body">
                          <div className="row-name">{action.name}</div>
                          <div className="row-meta">
                            <span>v{cur.version}</span>
                            {cur.locked && (
                              <span className="badge-locked">LOCKED</span>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </section>
            );
          })}
      </div>
    </aside>
  );
}

ListPane.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  actions: PropTypes.array.isRequired,
  selectedId: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  filter: PropTypes.object.isRequired,
  onFilter: PropTypes.func.isRequired,
};
